import { useState, useCallback } from "react";
import type { PackingItemResponseDto } from "@tabipla/shared/types";
import {
  filterByExistingNames,
  toggleSetItem,
  selectAllIndices,
  getSelectedItems,
} from "@/utils/packing-list";
import { TRIP_TYPE_TEMPLATES, type TripType } from "@/presentation/components/packing/constants";
import { useBulkCreatePackingItems } from "./usePacking";
import { useErrorHandler } from "./useErrorHandler";

export interface UsePackingTemplatesReturn {
  templateItems: { name: string }[];
  selectedTemplateItems: Set<number>;
  showTemplatePanel: boolean;
  selectedTripType: TripType | null;
  isBulkCreating: boolean;
  onSelectTemplate: (tripType: TripType) => void;
  onToggleTemplateItem: (index: number) => void;
  onSelectAllTemplateItems: () => void;
  onDeselectAllTemplateItems: () => void;
  onAddSelectedTemplateItems: () => Promise<void>;
  onCloseTemplatePanel: () => void;
}

/**
 * Hook for managing packing list templates.
 */
export function usePackingTemplates(
  tripId: string,
  items: PackingItemResponseDto[]
): UsePackingTemplatesReturn {
  const { handleError, showSuccess, t } = useErrorHandler();

  const bulkCreateMutation = useBulkCreatePackingItems(tripId);

  const [templateItems, setTemplateItems] = useState<{ name: string }[]>([]);
  const [selectedTemplateItems, setSelectedTemplateItems] = useState<Set<number>>(new Set());
  const [showTemplatePanel, setShowTemplatePanel] = useState(false);
  const [selectedTripType, setSelectedTripType] = useState<TripType | null>(null);

  const onSelectTemplate = useCallback(
    (tripType: TripType) => {
      const template = TRIP_TYPE_TEMPLATES[tripType];
      const newItems = filterByExistingNames(template.items, items);

      if (newItems.length === 0) return;

      setTemplateItems(newItems);
      setSelectedTemplateItems(selectAllIndices(newItems));
      setSelectedTripType(tripType);
      setShowTemplatePanel(true);
    },
    [items]
  );

  const onToggleTemplateItem = useCallback((index: number) => {
    setSelectedTemplateItems((prev) => toggleSetItem(prev, index));
  }, []);

  const onSelectAllTemplateItems = useCallback(() => {
    setSelectedTemplateItems(selectAllIndices(templateItems));
  }, [templateItems]);

  const onDeselectAllTemplateItems = useCallback(() => {
    setSelectedTemplateItems(new Set());
  }, []);

  const onAddSelectedTemplateItems = useCallback(async () => {
    const selectedItems = getSelectedItems(templateItems, selectedTemplateItems);

    if (selectedItems.length === 0) return;

    try {
      await bulkCreateMutation.mutateAsync({ items: selectedItems });
      setShowTemplatePanel(false);
      setTemplateItems([]);
      setSelectedTemplateItems(new Set());
      setSelectedTripType(null);
      showSuccess(
        "packing.templateAdded",
        t("packing.templateAdded", {
          count: selectedItems.length,
          defaultValue: `${selectedItems.length}件のテンプレートを追加しました`,
        })
      );
    } catch (error) {
      handleError(error, { message: "テンプレートの追加に失敗しました" });
    }
  }, [templateItems, selectedTemplateItems, bulkCreateMutation, showSuccess, handleError, t]);

  const onCloseTemplatePanel = useCallback(() => {
    setShowTemplatePanel(false);
    setTemplateItems([]);
    setSelectedTemplateItems(new Set());
    setSelectedTripType(null);
  }, []);

  return {
    templateItems,
    selectedTemplateItems,
    showTemplatePanel,
    selectedTripType,
    isBulkCreating: bulkCreateMutation.isPending,
    onSelectTemplate,
    onToggleTemplateItem,
    onSelectAllTemplateItems,
    onDeselectAllTemplateItems,
    onAddSelectedTemplateItems,
    onCloseTemplatePanel,
  };
}
